import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  GET_LATEST_FEDERAL_DOCUMENT_STATUSES_FOR_COMPANY,
  FederalDocumentStatuses,
  GetLatestFederalDocumentStatusesForCompanyQuery,
  GetLatestFederalDocumentStatusesForCompanyQueryVariables,
} from '../graphql';

type GetLatestFederalDocumentStatusesForCompanyType =
  | {
      documentStatuses: FederalDocumentStatuses;
      loading: false;
    }
  | {
      documentStatuses: null;
      loading: true;
    };

type GetLatestFederalDocumentStatusesForCompanyTypeWithSkip =
  | {
      documentStatuses: null;
      loading: false;
    }
  | GetLatestFederalDocumentStatusesForCompanyType;

export interface UseGetLatestFederalDocumentStatusesForCompanyOptions {
  skip?: boolean;
  companyId?: string;
}

export const useGetLatestFederalDocumentStatusesForCompany = ({
  companyId,
  skip,
}: UseGetLatestFederalDocumentStatusesForCompanyOptions): GetLatestFederalDocumentStatusesForCompanyTypeWithSkip => {
  const shouldSkip = skip || !companyId;
  const { data, error, loading } = useQuery<
    GetLatestFederalDocumentStatusesForCompanyQuery,
    GetLatestFederalDocumentStatusesForCompanyQueryVariables
  >(GET_LATEST_FEDERAL_DOCUMENT_STATUSES_FOR_COMPANY, {
    variables: { companyId: companyId! },
    skip: shouldSkip,
  });

  if (shouldSkip) {
    return { documentStatuses: null, loading: false };
  }

  if (loading) {
    return { documentStatuses: null, loading: true };
  }

  if (error) {
    throw error;
  }

  if (!data) {
    throw createError('MissingDocumentStatuses', 'Failed to load document statuses');
  }

  return {
    documentStatuses: data.getLatestFederalDocumentStatusesForCompany ?? null,
    loading,
  };
};
